import type { ReactNode } from "react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface AppLayoutProps {
  title: string;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  children: ReactNode;
}

export function AppLayout({
  title,
  searchPlaceholder = "Search...",
  onSearchChange,
  children,
}: AppLayoutProps) {
  const isMobile = useIsMobile();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    onSearchChange?.(value);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      <div className="border-b bg-background p-4 md:p-6 flex items-center justify-between gap-4 shrink-0 min-w-0">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {isMobile && searchOpen ? null : (
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold truncate">{title}</h1>
          )}
        </div>
        {searchPlaceholder && (
          <div className="flex items-center gap-2 md:gap-4 shrink-0">
            {isMobile ? (
              searchOpen ? (
                <div className="relative w-full flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder={searchPlaceholder}
                    className="pl-10 w-full"
                    value={searchValue}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                    onClick={() => {
                      setSearchOpen(false);
                      setSearchValue("");
                      handleSearchChange("");
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearchOpen(true)}
                  aria-label="Search"
                >
                  <Search className="h-5 w-5" />
                </Button>
              )
            ) : (
              <div className="relative w-full md:w-48 lg:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder={searchPlaceholder}
                  className="pl-10 w-full"
                  value={searchValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <main
        className={`flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 min-w-0 ${isMobile ? "pb-content-safe" : "pb-24"}`}
      >
        {children}
      </main>
    </div>
  );
}